import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Paper,
  alpha,
  styled,
  Alert,
  IconButton,
  Tooltip,
  Fade,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  Person as PersonIcon,
  CreditCard as CardIcon,
  Receipt as ReceiptIcon,
  Help as HelpIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(8px)',
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  },
}));

const SubscriptionCard = styled(Card)(({ theme, selected }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  border: selected ? `2px solid ${theme.palette.primary.main}` : '1px solid',
  borderColor: selected ? theme.palette.primary.main : theme.palette.divider,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

const PaymentAndSubscription = ({ formData, onChange, errors }) => {
  const dispatch = useDispatch();
  const { availableOptions } = useSelector((state) => state.onboarding);
  const { paymentSetup, subscription } = formData;
  const [localErrors, setLocalErrors] = useState({});

  const handleChange = (field) => (event) => {
    onChange('paymentSetup', { [field]: event.target.value });
  };

  const handleSubscriptionSelect = (plan) => {
    onChange('subscription', { selectedPlan: plan });
  };

  const validateAccountNumber = (value) => {
    if (!/^\d{9,18}$/.test(value)) {
      return 'Please enter a valid account number (9-18 digits)';
    }
    return null;
  };

  const validateIFSC = (value) => {
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value)) {
      return 'Please enter a valid IFSC code';
    }
    return null;
  };

  const validatePAN = (value) => {
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)) {
      return 'Please enter a valid PAN number';
    }
    return null;
  };

  const handleValidatedChange = (field, validator) => (event) => {
    const value = event.target.value.toUpperCase();
    const error = validator(value);
    setLocalErrors(prev => ({ ...prev, [field]: error }));
    if (!error) {
      onChange('paymentSetup', { [field]: value });
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
        Payment & Subscription
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <StyledPaper>
            <Typography variant="subtitle1" gutterBottom color="primary">
              Bank Account Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  label="Bank Name"
                  value={paymentSetup.bankName}
                  onChange={handleChange('bankName')}
                  error={!!errors.bankName}
                  helperText={errors.bankName}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BankIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  label="Account Holder Name"
                  value={paymentSetup.accountHolderName}
                  onChange={handleChange('accountHolderName')}
                  error={!!errors.accountHolderName}
                  helperText={errors.accountHolderName}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  label="Account Number"
                  value={paymentSetup.accountNumber}
                  onChange={handleValidatedChange('accountNumber', validateAccountNumber)}
                  error={!!errors.accountNumber || !!localErrors.accountNumber}
                  helperText={errors.accountNumber || localErrors.accountNumber}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CardIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  label="IFSC Code"
                  value={paymentSetup.ifscCode}
                  onChange={handleValidatedChange('ifscCode', validateIFSC)}
                  error={!!errors.ifscCode || !!localErrors.ifscCode}
                  helperText={errors.ifscCode || localErrors.ifscCode}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BankIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  label="PAN Number"
                  value={paymentSetup.panNumber}
                  onChange={handleValidatedChange('panNumber', validatePAN)}
                  error={!!errors.panNumber || !!localErrors.panNumber}
                  helperText={errors.panNumber || localErrors.panNumber}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ReceiptIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  label="GST Number (Optional)"
                  value={paymentSetup.gstNumber}
                  onChange={handleChange('gstNumber')}
                  error={!!errors.gstNumber}
                  helperText={errors.gstNumber}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ReceiptIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </StyledPaper>
        </Grid>

        <Grid item xs={12}>
          <StyledPaper>
            <Typography variant="subtitle1" gutterBottom color="primary">
              Payment Preferences
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!!errors.paymentCycle}>
                  <InputLabel>Payment Cycle</InputLabel>
                  <Select
                    value={paymentSetup.paymentCycle}
                    onChange={handleChange('paymentCycle')}
                    label="Payment Cycle"
                  >
                    {availableOptions?.paymentCycles?.map((cycle) => (
                      <MenuItem key={cycle.value} value={cycle.value}>
                        {cycle.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.paymentCycle && (
                    <FormHelperText>{errors.paymentCycle}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  label="Minimum Payout Amount"
                  type="number"
                  value={paymentSetup.minimumPayout}
                  onChange={handleChange('minimumPayout')}
                  error={!!errors.minimumPayout}
                  helperText={errors.minimumPayout}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MoneyIcon color="primary" />
                      </InputAdornment>
                    ),
                    inputProps: { min: 0 },
                  }}
                />
              </Grid>
            </Grid>
          </StyledPaper>
        </Grid>

        <Grid item xs={12}>
          <StyledPaper>
            <Typography variant="subtitle1" gutterBottom color="primary">
              Subscription Plans
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose a subscription plan that best suits your practice
            </Typography>
            <Grid container spacing={3}>
              {availableOptions?.subscriptionPlans?.map((plan) => (
                <Grid item xs={12} sm={6} md={4} key={plan.id}>
                  <SubscriptionCard
                    selected={subscription.selectedPlan?.id === plan.id}
                    onClick={() => handleSubscriptionSelect(plan)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" color="primary">
                          {plan.name}
                        </Typography>
                        <Chip
                          icon={<StarIcon />}
                          label={plan.type}
                          color={plan.type === 'PREMIUM' ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="h5" gutterBottom>
                        ₹{plan.price}/month
                      </Typography>
                      <List dense>
                        {plan.features.map((feature, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <CheckCircleIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={feature} />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                    <CardActions>
                      <Button
                        fullWidth
                        variant={subscription.selectedPlan?.id === plan.id ? 'contained' : 'outlined'}
                        color="primary"
                      >
                        {subscription.selectedPlan?.id === plan.id ? 'Selected' : 'Select Plan'}
                      </Button>
                    </CardActions>
                  </SubscriptionCard>
                </Grid>
              ))}
            </Grid>
          </StyledPaper>
        </Grid>
      </Grid>

      <Fade in timeout={500}>
        <Box sx={{ mt: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Payment and subscription setup helps us:
            </Typography>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>Process your earnings securely</li>
              <li>Ensure timely payments</li>
              <li>Maintain financial compliance</li>
              <li>Provide you with premium features</li>
            </ul>
          </Alert>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              Need help with payment setup?
            </Typography>
            <Tooltip title="Contact our support team for assistance">
              <IconButton size="small" color="primary">
                <HelpIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Fade>

      <Typography 
        variant="body2" 
        color="text.secondary" 
        sx={{ mt: 3, fontStyle: 'italic' }}
      >
        * Required fields are marked with an asterisk. Your payment information is encrypted and secure.
      </Typography>
    </Box>
  );
};

export default PaymentAndSubscription; 